import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useHelpdeskLanguage } from '../../context/HelpdeskLanguageContext';
import './Helpdesk.css';

const translations = {
    en: {
        title: 'Department Management',
        addDepartment: 'Add Department',
        name: 'Name (English)',
        nameAr: 'Name (Arabic)',
        description: 'Description',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        actions: 'Actions',
        edit: 'Edit',
        delete: 'Delete',
        save: 'Save',
        cancel: 'Cancel',
        confirmDelete: 'Are you sure you want to delete this department?',
        noDepartments: 'No departments found',
        loading: 'Loading...',
        created: 'Department created successfully',
        updated: 'Department updated successfully',
        deleted: 'Department deleted successfully',
        error: 'An error occurred',
        required: 'Name is required',
        ticketsAssigned: 'Cannot delete - tickets are assigned to this department',
        agentAssignment: 'Agent Assignment',
        assignAgents: 'Assign Departments to Agent',
        selectAgent: 'Select Agent',
        assignedAgents: 'Assigned Agents',
        viewAll: 'Can View All Tickets',
        departments: 'Departments',
        clickToAssign: 'Click on an agent to assign departments',
        // Sub-department translations
        subDepartments: 'Sub-Departments',
        addSubDepartment: 'Add Sub-Department',
        subDeptName: 'Name (English)',
        subDeptNameAr: 'Name (Arabic)',
        subDeptDescription: 'Description',
        parentDepartment: 'Department',
        selectDepartment: 'Select Department',
        noSubDepartments: 'No sub-departments found',
        subDeptCreated: 'Sub-department created successfully',
        subDeptUpdated: 'Sub-department updated successfully',
        subDeptDeleted: 'Sub-department deleted successfully',
        subDeptRequired: 'Name and department are required',
        confirmDeleteSubDept: 'Are you sure you want to delete this sub-department?',
        assignedSubDepartments: 'Sub-Departments',
        roundRobin: 'Round Robin',
        totalAssigned: 'Total Assigned',
        lastAssigned: 'Last Assigned',
        resetRoundRobin: 'Reset',
        noAgentsAssigned: 'No agents assigned',
        agentsCount: 'Agents',
        filterByDepartment: 'Filter by Department',
        allDepartments: 'All Departments',
        subDeptDeactivated: 'Sub-department deactivated (has existing tickets)'
    },
    ar: {
        title: 'إدارة الأقسام',
        addDepartment: 'إضافة قسم',
        name: 'الاسم (إنجليزي)',
        nameAr: 'الاسم (عربي)',
        description: 'الوصف',
        status: 'الحالة',
        active: 'نشط',
        inactive: 'غير نشط',
        actions: 'الإجراءات',
        edit: 'تعديل',
        delete: 'حذف',
        save: 'حفظ',
        cancel: 'إلغاء',
        confirmDelete: 'هل أنت متأكد من حذف هذا القسم؟',
        noDepartments: 'لا توجد أقسام',
        loading: 'جاري التحميل...',
        created: 'تم إنشاء القسم بنجاح',
        updated: 'تم تحديث القسم بنجاح',
        deleted: 'تم حذف القسم بنجاح',
        error: 'حدث خطأ',
        required: 'الاسم مطلوب',
        ticketsAssigned: 'لا يمكن الحذف - توجد تذاكر مرتبطة بهذا القسم',
        agentAssignment: 'ربط الموظفين بالأقسام',
        assignAgents: 'تعيين الأقسام للموظف',
        selectAgent: 'اختر موظف',
        assignedAgents: 'الموظفين المعينين',
        viewAll: 'يمكنه رؤية جميع التذاكر',
        departments: 'الأقسام',
        clickToAssign: 'اضغط على الموظف لتعيين الأقسام',
        // Sub-department translations
        subDepartments: 'التقسيمات الفرعية',
        addSubDepartment: 'إضافة تقسيم فرعي',
        subDeptName: 'الاسم (إنجليزي)',
        subDeptNameAr: 'الاسم (عربي)',
        subDeptDescription: 'الوصف',
        parentDepartment: 'القسم الرئيسي',
        selectDepartment: 'اختر القسم',
        noSubDepartments: 'لا توجد تقسيمات فرعية',
        subDeptCreated: 'تم إنشاء التقسيم الفرعي بنجاح',
        subDeptUpdated: 'تم تحديث التقسيم الفرعي بنجاح',
        subDeptDeleted: 'تم حذف التقسيم الفرعي بنجاح',
        subDeptRequired: 'الاسم والقسم مطلوبان',
        confirmDeleteSubDept: 'هل أنت متأكد من حذف هذا التقسيم الفرعي؟',
        assignedSubDepartments: 'التقسيمات الفرعية',
        roundRobin: 'التوزيع بالتبادل',
        totalAssigned: 'إجمالي التعيينات',
        lastAssigned: 'آخر تعيين',
        resetRoundRobin: 'إعادة تعيين',
        noAgentsAssigned: 'لا يوجد موظفين معينين',
        agentsCount: 'موظفين',
        filterByDepartment: 'فلترة حسب القسم',
        allDepartments: 'جميع الأقسام',
        subDeptDeactivated: 'تم تعطيل التقسيم الفرعي (توجد تذاكر مرتبطة)'
    }
};

function HelpdeskDepartments() {
    const { language, isRTL } = useHelpdeskLanguage();
    const t = (key) => translations[language]?.[key] || translations.en[key] || key;
    
    const [departments, setDepartments] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ text: '', type: '' });
    
    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        name_ar: '',
        description: '',
        is_active: true
    });
    
    // Agent assignment state
    const [showAgentModal, setShowAgentModal] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [agentDepartments, setAgentDepartments] = useState([]);
    const [canViewAll, setCanViewAll] = useState(false);

    // ========== Sub-Department State ==========
    const [subDepartments, setSubDepartments] = useState([]);
    const [subDeptLoading, setSubDeptLoading] = useState(false);
    const [showSubDeptForm, setShowSubDeptForm] = useState(false);
    const [editingSubDeptId, setEditingSubDeptId] = useState(null);
    const [subDeptFormData, setSubDeptFormData] = useState({
        department_id: '',
        name: '',
        name_ar: '',
        description: ''
    });
    const [subDeptFilter, setSubDeptFilter] = useState('');

    // Agent sub-department assignment state (inside agent modal)
    const [agentSubDepartments, setAgentSubDepartments] = useState([]);
    const [availableSubDepts, setAvailableSubDepts] = useState([]);

    const token = localStorage.getItem('token');

    useEffect(() => {
        loadDepartments();
        loadAgents();
        loadSubDepartments();
    }, []);

    const loadDepartments = async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/v1/helpdesk-agent/departments', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setDepartments(data.departments);
            }
        } catch (error) {
            console.error('Failed to load departments:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAgents = async () => {
        try {
            const response = await fetch('/api/v1/helpdesk-agent/agents', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setAgents(data.agents);
            }
        } catch (error) {
            console.error('Failed to load agents:', error);
        }
    };

    const loadAgentDepartments = async (agentId) => {
        try {
            const response = await fetch(`/api/v1/helpdesk-agent/agents/${agentId}/departments`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setAgentDepartments(data.departments.map(d => d.id));
                setCanViewAll(data.canViewAll);
            }
        } catch (error) {
            console.error('Failed to load agent departments:', error);
        }
    };

    // ========== Sub-Department Functions ==========

    const loadSubDepartments = async () => {
        try {
            setSubDeptLoading(true);
            const response = await fetch('/api/v1/helpdesk-agent/sub-departments', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setSubDepartments(data.sub_departments);
            }
        } catch (error) {
            console.error('Failed to load sub-departments:', error);
        } finally {
            setSubDeptLoading(false);
        }
    };

    const loadAgentSubDepartments = async (agentId) => {
        try {
            const response = await fetch(`/api/v1/helpdesk-agent/agents/${agentId}/sub-departments`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setAgentSubDepartments(data.sub_departments.map(sd => sd.id));
            }
        } catch (error) {
            console.error('Failed to load agent sub-departments:', error);
            setAgentSubDepartments([]);
        }
    };

    const handleSubDeptSubmit = async (e) => {
        e.preventDefault();
        
        if (!subDeptFormData.name.trim() || !subDeptFormData.department_id) {
            showMessage(t('subDeptRequired'), 'error');
            return;
        }

        try {
            const url = editingSubDeptId 
                ? `/api/v1/helpdesk-agent/sub-departments/${editingSubDeptId}`
                : '/api/v1/helpdesk-agent/sub-departments';
            
            const response = await fetch(url, {
                method: editingSubDeptId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(subDeptFormData)
            });

            const data = await response.json();
            
            if (data.success) {
                showMessage(editingSubDeptId ? t('subDeptUpdated') : t('subDeptCreated'));
                loadSubDepartments();
                resetSubDeptForm();
            } else {
                showMessage(data.message || t('error'), 'error');
            }
        } catch (error) {
            console.error('Failed to save sub-department:', error);
            showMessage(t('error'), 'error');
        }
    };

    const handleSubDeptEdit = (subDept) => {
        setEditingSubDeptId(subDept.id);
        setSubDeptFormData({
            department_id: subDept.department_id,
            name: subDept.name,
            name_ar: subDept.name_ar || '',
            description: subDept.description || ''
        });
        setShowSubDeptForm(true);
    };

    const handleSubDeptDelete = async (id) => {
        if (!window.confirm(t('confirmDeleteSubDept'))) return;

        try {
            const response = await fetch(`/api/v1/helpdesk-agent/sub-departments/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            
            if (data.success) {
                showMessage(data.message.includes('deactivated') ? t('subDeptDeactivated') : t('subDeptDeleted'));
                loadSubDepartments();
            } else {
                showMessage(data.message || t('error'), 'error');
            }
        } catch (error) {
            console.error('Failed to delete sub-department:', error);
            showMessage(t('error'), 'error');
        }
    };

    const resetSubDeptForm = () => {
        setShowSubDeptForm(false);
        setEditingSubDeptId(null);
        setSubDeptFormData({
            department_id: '',
            name: '',
            name_ar: '',
            description: ''
        });
    };

    const handleSubDeptToggle = (subDeptId) => {
        setAgentSubDepartments(prev => 
            prev.includes(subDeptId) 
                ? prev.filter(id => id !== subDeptId)
                : [...prev, subDeptId]
        );
    };

    const filteredSubDepartments = subDeptFilter 
        ? subDepartments.filter(sd => String(sd.department_id) === String(subDeptFilter))
        : subDepartments;

    // Get sub-departments relevant to agent's assigned departments
    const getAgentAvailableSubDepts = () => {
        return subDepartments.filter(sd => agentDepartments.includes(sd.department_id));
    };

    // ========== Common Functions ==========

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            showMessage(t('required'), 'error');
            return;
        }

        try {
            const url = editingId 
                ? `/api/v1/helpdesk-agent/departments/${editingId}`
                : '/api/v1/helpdesk-agent/departments';
            
            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();
            
            if (data.success) {
                showMessage(editingId ? t('updated') : t('created'));
                loadDepartments();
                resetForm();
            } else {
                showMessage(data.message || t('error'), 'error');
            }
        } catch (error) {
            console.error('Failed to save department:', error);
            showMessage(t('error'), 'error');
        }
    };

    const handleEdit = (dept) => {
        setEditingId(dept.id);
        setFormData({
            name: dept.name,
            name_ar: dept.name_ar || '',
            description: dept.description || '',
            is_active: dept.is_active === 1
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('confirmDelete'))) return;

        try {
            const response = await fetch(`/api/v1/helpdesk-agent/departments/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            
            if (data.success) {
                showMessage(t('deleted'));
                loadDepartments();
            } else {
                showMessage(data.message || t('error'), 'error');
            }
        } catch (error) {
            console.error('Failed to delete department:', error);
            showMessage(t('error'), 'error');
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            name: '',
            name_ar: '',
            description: '',
            is_active: true
        });
    };

    const handleAgentSelect = async (agent) => {
        setSelectedAgent(agent);
        await loadAgentDepartments(agent.id);
        await loadAgentSubDepartments(agent.id);
        setShowAgentModal(true);
    };

    const handleDepartmentToggle = (deptId) => {
        setAgentDepartments(prev => {
            const newDepts = prev.includes(deptId) 
                ? prev.filter(id => id !== deptId)
                : [...prev, deptId];
            
            // If a department is unchecked, also uncheck its sub-departments
            if (!newDepts.includes(deptId)) {
                const subDeptsToRemove = subDepartments
                    .filter(sd => sd.department_id === deptId)
                    .map(sd => sd.id);
                setAgentSubDepartments(prev => prev.filter(id => !subDeptsToRemove.includes(id)));
            }
            
            return newDepts;
        });
    };

    const saveAgentDepartments = async () => {
        if (!selectedAgent) return;

        try {
            // Save departments
            const deptResponse = await fetch(`/api/v1/helpdesk-agent/agents/${selectedAgent.id}/departments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    departmentIds: agentDepartments,
                    canViewAll
                })
            });

            const deptData = await deptResponse.json();

            // Save sub-departments
            const subDeptResponse = await fetch(`/api/v1/helpdesk-agent/agents/${selectedAgent.id}/sub-departments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    sub_department_ids: agentSubDepartments
                })
            });

            const subDeptData = await subDeptResponse.json();
            
            if (deptData.success && subDeptData.success) {
                showMessage(t('updated'));
                setShowAgentModal(false);
            } else {
                showMessage(deptData.message || subDeptData.message || t('error'), 'error');
            }
        } catch (error) {
            console.error('Failed to save agent assignments:', error);
            showMessage(t('error'), 'error');
        }
    };

    // Group sub-departments by department for display in agent modal
    const groupedSubDepts = () => {
        const available = getAgentAvailableSubDepts();
        const grouped = {};
        available.forEach(sd => {
            const deptName = language === 'ar' && sd.department_name_ar 
                ? sd.department_name_ar 
                : sd.department_name;
            if (!grouped[sd.department_id]) {
                grouped[sd.department_id] = {
                    name: deptName,
                    items: []
                };
            }
            grouped[sd.department_id].items.push(sd);
        });
        return grouped;
    };

    return (
        <Layout>
            <div className="hd-container" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="hd-header">
                    <h1>🏢 {t('title')}</h1>
                </div>

                {message.text && (
                    <div className={`hd-message ${message.type === 'error' ? 'hd-message-error' : 'hd-message-success'}`}>
                        {message.text}
                    </div>
                )}

                {/* ==================== Departments Section ==================== */}
                <div className="hd-card" style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3>{t('departments')}</h3>
                        <button 
                            className="hd-btn hd-btn-primary"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? t('cancel') : `+ ${t('addDepartment')}`}
                        </button>
                    </div>

                    {showForm && (
                        <form onSubmit={handleSubmit} style={{ 
                            background: 'rgba(255,255,255,0.05)', 
                            padding: '20px', 
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>
                                        {t('name')} *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="hd-input"
                                        required
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>
                                        {t('nameAr')}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name_ar}
                                        onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                                        className="hd-input"
                                        dir="rtl"
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>
                                    {t('description')}
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="hd-textarea"
                                    rows={2}
                                />
                            </div>
                            <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                    <span style={{ color: '#ccc' }}>{t('active')}</span>
                                </label>
                            </div>
                            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                                <button type="submit" className="hd-btn hd-btn-primary">
                                    {t('save')}
                                </button>
                                <button type="button" className="hd-btn hd-btn-secondary" onClick={resetForm}>
                                    {t('cancel')}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Departments Table */}
                    {loading ? (
                        <p style={{ textAlign: 'center', color: '#888' }}>{t('loading')}</p>
                    ) : departments.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#888' }}>{t('noDepartments')}</p>
                    ) : (
                        <table className="hd-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('name')}</th>
                                    <th>{t('nameAr')}</th>
                                    <th>{t('description')}</th>
                                    <th>{t('status')}</th>
                                    <th>{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {departments.map((dept, index) => (
                                    <tr key={dept.id}>
                                        <td>{index + 1}</td>
                                        <td>{dept.name}</td>
                                        <td dir="rtl">{dept.name_ar || '-'}</td>
                                        <td>{dept.description || '-'}</td>
                                        <td>
                                            <span className={`hd-badge ${dept.is_active ? 'hd-badge-success' : 'hd-badge-danger'}`}>
                                                {dept.is_active ? t('active') : t('inactive')}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button 
                                                    className="hd-btn hd-btn-sm hd-btn-secondary"
                                                    onClick={() => handleEdit(dept)}
                                                >
                                                    ✏️
                                                </button>
                                                <button 
                                                    className="hd-btn hd-btn-sm hd-btn-danger"
                                                    onClick={() => handleDelete(dept.id)}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ==================== Sub-Departments Section ==================== */}
                <div className="hd-card" style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3>📂 {t('subDepartments')}</h3>
                        <button 
                            className="hd-btn hd-btn-primary"
                            onClick={() => setShowSubDeptForm(!showSubDeptForm)}
                        >
                            {showSubDeptForm ? t('cancel') : `+ ${t('addSubDepartment')}`}
                        </button>
                    </div>

                    {showSubDeptForm && (
                        <form onSubmit={handleSubDeptSubmit} style={{ 
                            background: 'rgba(255,255,255,0.05)', 
                            padding: '20px', 
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>
                                        {t('parentDepartment')} *
                                    </label>
                                    <select
                                        value={subDeptFormData.department_id}
                                        onChange={(e) => setSubDeptFormData({ ...subDeptFormData, department_id: e.target.value })}
                                        className="hd-input"
                                        required
                                    >
                                        <option value="">{t('selectDepartment')}</option>
                                        {departments.filter(d => d.is_active).map(dept => (
                                            <option key={dept.id} value={dept.id}>
                                                {language === 'ar' && dept.name_ar ? dept.name_ar : dept.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>
                                        {t('subDeptName')} *
                                    </label>
                                    <input
                                        type="text"
                                        value={subDeptFormData.name}
                                        onChange={(e) => setSubDeptFormData({ ...subDeptFormData, name: e.target.value })}
                                        className="hd-input"
                                        required
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>
                                        {t('subDeptNameAr')}
                                    </label>
                                    <input
                                        type="text"
                                        value={subDeptFormData.name_ar}
                                        onChange={(e) => setSubDeptFormData({ ...subDeptFormData, name_ar: e.target.value })}
                                        className="hd-input"
                                        dir="rtl"
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>
                                    {t('subDeptDescription')}
                                </label>
                                <textarea
                                    value={subDeptFormData.description}
                                    onChange={(e) => setSubDeptFormData({ ...subDeptFormData, description: e.target.value })}
                                    className="hd-textarea"
                                    rows={2}
                                />
                            </div>
                            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                                <button type="submit" className="hd-btn hd-btn-primary">
                                    {t('save')}
                                </button>
                                <button type="button" className="hd-btn hd-btn-secondary" onClick={resetSubDeptForm}>
                                    {t('cancel')}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Filter by department */}
                    <div style={{ marginBottom: '15px' }}>
                        <select
                            value={subDeptFilter}
                            onChange={(e) => setSubDeptFilter(e.target.value)}
                            className="hd-input"
                            style={{ maxWidth: '300px' }}
                        >
                            <option value="">{t('allDepartments')}</option>
                            {departments.filter(d => d.is_active).map(dept => (
                                <option key={dept.id} value={dept.id}>
                                    {language === 'ar' && dept.name_ar ? dept.name_ar : dept.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Sub-Departments Table */}
                    {subDeptLoading ? (
                        <p style={{ textAlign: 'center', color: '#888' }}>{t('loading')}</p>
                    ) : filteredSubDepartments.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#888' }}>{t('noSubDepartments')}</p>
                    ) : (
                        <table className="hd-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('parentDepartment')}</th>
                                    <th>{t('subDeptName')}</th>
                                    <th>{t('subDeptNameAr')}</th>
                                    <th>{t('subDeptDescription')}</th>
                                    <th>{t('status')}</th>
                                    <th>{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubDepartments.map((sd, index) => (
                                    <tr key={sd.id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <span style={{ 
                                                background: 'rgba(59, 130, 246, 0.15)', 
                                                color: '#60a5fa',
                                                padding: '3px 10px', 
                                                borderRadius: '4px',
                                                fontSize: '12px'
                                            }}>
                                                {language === 'ar' && sd.department_name_ar 
                                                    ? sd.department_name_ar 
                                                    : sd.department_name}
                                            </span>
                                        </td>
                                        <td>{sd.name}</td>
                                        <td dir="rtl">{sd.name_ar || '-'}</td>
                                        <td>{sd.description || '-'}</td>
                                        <td>
                                            <span className={`hd-badge ${sd.is_active ? 'hd-badge-success' : 'hd-badge-danger'}`}>
                                                {sd.is_active ? t('active') : t('inactive')}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button 
                                                    className="hd-btn hd-btn-sm hd-btn-secondary"
                                                    onClick={() => handleSubDeptEdit(sd)}
                                                >
                                                    ✏️
                                                </button>
                                                <button 
                                                    className="hd-btn hd-btn-sm hd-btn-danger"
                                                    onClick={() => handleSubDeptDelete(sd.id)}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ==================== Agent Assignment Section ==================== */}
                <div className="hd-card">
                    <h3 style={{ marginBottom: '10px' }}>👥 {t('agentAssignment')}</h3>
                    <p style={{ color: '#888', marginBottom: '15px', fontSize: '14px' }}>{t('clickToAssign')}</p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                        {agents.map(agent => (
                            <div 
                                key={agent.id}
                                style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    border: '1px solid transparent'
                                }}
                                onClick={() => handleAgentSelect(agent)}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                            >
                                <div style={{ fontWeight: 600, marginBottom: '5px' }}>
                                    {agent.full_name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#888' }}>
                                    <span style={{ 
                                        background: agent.role === 'admin' ? '#ef4444' : agent.role === 'supervisor' ? '#f59e0b' : '#3b82f6',
                                        color: 'white',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        marginRight: '8px'
                                    }}>
                                        {agent.role}
                                    </span>
                                    {agent.email}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ==================== Agent Assignment Modal ==================== */}
                {showAgentModal && selectedAgent && (
                    <div className="hd-modal-overlay" onClick={() => setShowAgentModal(false)}>
                        <div className="hd-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
                            <h3 style={{ marginBottom: '20px' }}>
                                {t('assignAgents')}: {selectedAgent.full_name}
                            </h3>
                            
                            {/* View All Checkbox */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px',
                                    padding: '15px',
                                    background: canViewAll ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255,255,255,0.05)',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    border: canViewAll ? '1px solid #22c55e' : '1px solid transparent'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={canViewAll}
                                        onChange={(e) => setCanViewAll(e.target.checked)}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <span style={{ fontWeight: 600 }}>🌐 {t('viewAll')}</span>
                                </label>
                            </div>

                            {/* Departments */}
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ marginBottom: '10px', color: '#888' }}>🏢 {t('departments')}</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                                    {departments.filter(d => d.is_active).map(dept => (
                                        <label 
                                            key={dept.id}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '10px',
                                                padding: '12px',
                                                background: agentDepartments.includes(dept.id) 
                                                    ? 'rgba(59, 130, 246, 0.2)' 
                                                    : 'rgba(255,255,255,0.05)',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                border: agentDepartments.includes(dept.id) 
                                                    ? '1px solid #3b82f6' 
                                                    : '1px solid transparent'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={agentDepartments.includes(dept.id)}
                                                onChange={() => handleDepartmentToggle(dept.id)}
                                                style={{ width: '16px', height: '16px' }}
                                            />
                                            <span>{language === 'ar' && dept.name_ar ? dept.name_ar : dept.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Sub-Departments (grouped by department) */}
                            {Object.keys(groupedSubDepts()).length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: '#888' }}>📂 {t('assignedSubDepartments')}</h4>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                        {Object.entries(groupedSubDepts()).map(([deptId, group]) => (
                                            <div key={deptId} style={{ marginBottom: '12px' }}>
                                                <div style={{ 
                                                    fontSize: '12px', 
                                                    color: '#60a5fa', 
                                                    fontWeight: 600, 
                                                    marginBottom: '6px',
                                                    padding: '4px 8px',
                                                    background: 'rgba(59, 130, 246, 0.1)',
                                                    borderRadius: '4px',
                                                    display: 'inline-block'
                                                }}>
                                                    {group.name}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {group.items.map(sd => (
                                                        <label 
                                                            key={sd.id}
                                                            style={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: '10px',
                                                                padding: '10px 12px',
                                                                paddingLeft: isRTL ? '12px' : '24px',
                                                                paddingRight: isRTL ? '24px' : '12px',
                                                                background: agentSubDepartments.includes(sd.id) 
                                                                    ? 'rgba(168, 85, 247, 0.15)' 
                                                                    : 'rgba(255,255,255,0.03)',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                border: agentSubDepartments.includes(sd.id) 
                                                                    ? '1px solid #a855f7' 
                                                                    : '1px solid transparent',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={agentSubDepartments.includes(sd.id)}
                                                                onChange={() => handleSubDeptToggle(sd.id)}
                                                                style={{ width: '15px', height: '15px' }}
                                                            />
                                                            <span>{language === 'ar' && sd.name_ar ? sd.name_ar : sd.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button 
                                    className="hd-btn hd-btn-secondary"
                                    onClick={() => setShowAgentModal(false)}
                                >
                                    {t('cancel')}
                                </button>
                                <button 
                                    className="hd-btn hd-btn-primary"
                                    onClick={saveAgentDepartments}
                                >
                                    {t('save')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default HelpdeskDepartments;